import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CheckCircle2, CircleDashed, Copy, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const LEVELS = [["cp_postal", "Postal (cheapest, untracked)"], ["cp_ground", "Ground (tracked)"], ["cp_saver", "Express saver (tracked)"], ["cp_fast", "Express fast (tracked)"]];

function Status({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-3" data-testid={`print-status-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {ok ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#4F7A5C]" /> : <CircleDashed className="mt-0.5 h-5 w-5 shrink-0 text-[#C9A227]" />}
      <div><p className="font-medium">{label}</p><p className="text-sm text-[#5E5868]">{hint}</p></div>
    </div>
  );
}

function SkuPicker({ slot, label, value, products, fallback, onChange }) {
  const isPoster = slot === "poster";
  const list = products.filter((p) => isPoster ? /poster|print|photo_?paper|canvas/i.test(`${p.reference} ${p.category} ${p.name}`) : /book|photobook|album/i.test(`${p.reference} ${p.category} ${p.name}`));
  const options = list.length ? list : products;
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#8C8498]">{label}</Label>
      {products.length ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`print-sku-${slot}`} className="mt-1 h-10 w-full rounded-md border border-[#E8E2D9] bg-white px-3 text-sm">
          <option value="">{fallback ? `Default · ${fallback}` : "— not set (orders will wait for you) —"}</option>
          {options.map((p) => <option key={p.reference} value={p.reference}>{p.name} · {p.reference}{p.from_price ? ` · from ${p.from_price} ${p.currency}` : ""}</option>)}
        </select>
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={fallback ? `Default · ${fallback}` : "e.g. photobook_cw_s210_s_fc"} data-testid={`print-sku-${slot}`} className="mt-1 bg-white" />
      )}
    </div>
  );
}

export default function AdminPrint() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({ skus: {}, shipping_level: "cp_postal", paper_option: "", support_email: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/print").then((r) => { setData(r.data); setCfg({ skus: {}, shipping_level: "cp_postal", paper_option: "", support_email: "", ...r.data.config }); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/admin/print", cfg); toast.success("Print partner settings saved"); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const mapped = Object.keys(data?.slots || {}).filter((s) => data?.config?.skus?.[s] || data?.defaults?.[s]).length;
  const copy = () => { navigator.clipboard?.writeText(data.webhook_url); toast.success("Webhook URL copied"); };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="admin-print-page">
        <p className="overline">Admin</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight">Print partner</h1>
        <p className="mt-3 max-w-2xl text-[#5E5868]">Cloudprinter prints and ships every paid book or poster from a facility in India. Once the three steps below are green, orders flow through with no manual work.</p>
        <p className="mt-2 text-sm"><Link to="/admin/orders" className="text-[#3B5BDB] underline-offset-4 hover:underline" data-testid="print-back-orders">← Orders board</Link></p>

        {data && (
          <section className="paper mt-8 space-y-5 rounded-lg p-6" data-testid="print-status-card">
            <Status ok={data.live} label="API key" hint={data.live ? "Cloudprinter CloudCore key is set — orders are submitted automatically." : "No key yet: orders are MOCKED and emailed to you for manual printing. Ask the assistant to add CLOUDPRINTER_API_KEY."} />
            <Status ok={mapped >= 1} label="Products mapped" hint={mapped ? `${mapped} of ${Object.keys(data.slots).length} sizes mapped to Cloudprinter products (hardcover casewrap books + A3 poster by default).` : "Choose which Cloudprinter product each book size and the poster should print as."} />
            <Status ok={data.webhook_configured} label="Status webhook" hint={data.webhook_configured ? "Printer updates (printing, shipped + tracking, delivered) arrive automatically." : "Add CLOUDPRINTER_WEBHOOK_KEY and paste the URL below into Cloudprinter → CloudSignal."} />
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-[#F5F0E8] px-3 py-2 text-xs">
              <span className="text-[#8C8498]">Webhook URL</span><code className="break-all font-mono" data-testid="print-webhook-url">{data.webhook_url}</code>
              <button type="button" onClick={copy} className="ml-auto inline-flex items-center gap-1 text-[#3B5BDB]" data-testid="print-webhook-copy"><Copy className="h-3.5 w-3.5" />Copy</button>
            </div>
            {data.error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="print-products-error">Couldn't load products from Cloudprinter: {data.error}</p>}
          </section>
        )}

        {data && (
          <form onSubmit={save} className="paper mt-6 space-y-5 rounded-lg p-6" data-testid="print-settings-form">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Product mapping</h2>
              <button type="button" onClick={load} className="inline-flex items-center gap-1 text-xs text-[#3B5BDB]" data-testid="print-reload"><RefreshCw className="h-3.5 w-3.5" />Refresh list</button>
            </div>
            {!data.products.length && <p className="text-sm text-[#8C8498]">{data.live ? "No products are enabled on your Cloudprinter account yet — enable photo books & posters in their dashboard, then refresh." : "Product list appears once the API key is set. You can type product references manually meanwhile."}</p>}
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(data.slots).map(([slot, label]) => (
                <SkuPicker key={slot} slot={slot} label={label} value={cfg.skus?.[slot]} products={data.products} fallback={data.defaults?.[slot]} onChange={(v) => setCfg({ ...cfg, skus: { ...cfg.skus, [slot]: v } })} />
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8C8498]">Shipping level</Label>
                <select value={cfg.shipping_level} onChange={(e) => setCfg({ ...cfg, shipping_level: e.target.value })} data-testid="print-shipping-level" className="mt-1 h-10 w-full rounded-md border border-[#E8E2D9] bg-white px-3 text-sm">
                  {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8C8498]">Book paper (optional)</Label>
                <Input value={cfg.paper_option} onChange={(e) => setCfg({ ...cfg, paper_option: e.target.value })} placeholder="Default · pageblock_170mcg" data-testid="print-paper-option" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#8C8498]">Support email for printer</Label>
                <Input type="email" value={cfg.support_email} onChange={(e) => setCfg({ ...cfg, support_email: e.target.value })} placeholder="defaults to admin email" data-testid="print-support-email" className="mt-1 bg-white" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="print-save-btn"><Printer className="h-4 w-4" />{busy ? "Saving…" : "Save settings"}</button>
          </form>
        )}
      </main>
    </div>
  );
}
